// @ts-check
// Clique no gráfico abre a lista filtrada (contrato 07, seção 10), lado da tela: cada ponto que abre uma lista é um
// <a href="/drilldown?d=<receita>"> de verdade, então Ctrl/Cmd+clique, clique do meio e "Copiar endereço do link" funcionam
// sozinhos. O clique simples pede o ponto ao servidor (POST) e navega para a lista, empilhando o histórico para o
// "Voltar" do navegador devolver o painel com os mesmos filtros. No toque, o primeiro toque fixa a dica com "Ver
// registros" e o segundo abre. Nos gráficos em SVG, o gráfico é uma parada só de Tab e as setas percorrem os pontos.
import { html } from 'htm/preact';
import { createContext } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { isMockEnabled } from '../../data/api-client.js';
import { resolveDrilldown } from '../../data/dashboards.js';
import { dismissSnackbar, enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import {
  buildPointHref, createDrillableIndex, drillableValueOf, pointKeyOf, recipeTextOfHref, shownValueOfHref,
} from '../../lib/drilldown-links.js';
import { chartKeyboardAction } from '../../lib/chart-keyboard.js';
import { t } from '../../i18n/index.js';

export { buildPointHref };

/**
 * @typedef {import('../../../../shared/dashboards/drilldown-recipe.js').DrilldownPoint} DrilldownPoint
 * @typedef {{ href: string, value: number }} DrillTarget
 * @typedef {{ widgetTitle: string, report: string | null, widgetType: string, isAdditive: boolean,
 *   target: (point: DrilldownPoint, explicitValue?: number | null) => DrillTarget | null,
 *   canOpen: (point: DrilldownPoint) => boolean }} DrillController
 * @typedef {{ id: string, categoryIndex: number, seriesIndex: number, href: string | null, resolveHref?: () => string | null }} ChartPoint
 */

// operações cuja faixa da categoria (todas as séries juntas) é a soma de um conjunto
const ADDITIVE_OPERATIONS = new Set(['COUNT', 'SUM', 'COUNT_EMPTY', 'COUNT_NOT_EMPTY', 'COUNT_TRUE', 'COUNT_FALSE']);

/** @type {import('preact').Context<DrillController | null>} */
export const DrilldownContext = createContext(/** @type {DrillController | null} */ (null));

/** Controle do clique do widget em volta (null: nada é clicável, como no modo de edição). */
export const useDrilldown = () => useContext(DrilldownContext);

/**
 * Controle do clique de um widget desenhado: só com o widget salvo, fora da edição e com o mapa `drilldown.parts` do
 * servidor, que diz o que este papel pode abrir.
 * @param {{ dashboardId: string | null | undefined, widget: { id?: string | null, type: string, title?: string, configuration?: Record<string, any> | null },
 *   filters: Record<string, unknown>, data: any }} input
 * @returns {DrillController | null}
 */
export function createDrillController({ dashboardId, widget, filters, data }) {
  const parts = data?.drilldown?.parts;
  if (!dashboardId || !widget?.id || !parts || typeof parts !== 'object' || Object.keys(parts).length === 0) return null;
  const index = createDrillableIndex(widget, data);
  const configuration = widget.configuration ?? {};
  const mock = isMockEnabled();
  /** @type {Map<string, string | null>} */
  const hrefCache = new Map();
  /** @param {DrilldownPoint} point @param {number | undefined} value @param {number | null} shownValue */
  const hrefOf = (point, value, shownValue) => {
    const key = pointKeyOf(point);
    if (!hrefCache.has(key)) hrefCache.set(key, buildPointHref({ id: dashboardId }, widget, filters, point, { value, shownValue, mock }));
    return hrefCache.get(key) ?? null;
  };
  return {
    widgetTitle: widget.title || t('dashboards.widget.untitled'),
    report: configuration.report ?? configuration.metric ?? null,
    widgetType: widget.type,
    isAdditive: widget.type === 'KPI' || ADDITIVE_OPERATIONS.has(configuration.operation ?? 'COUNT'),
    target(point, explicitValue) {
      const value = drillableValueOf(index, parts, point, explicitValue);
      if (value === null) return null;
      // o número do cartão vai no link quando é o mesmo que o servidor exibe (o da lista de pontos); a faixa da
      // categoria e o mapa na medida escolhida informam outro valor e não comparam
      const href = hrefOf(point, value, explicitValue === undefined ? value : null);
      return href ? { href, value } : null;
    },
    // o papel abre este tipo de ponto (os pontos de CEP do mapa montam o link só no ponteiro ou no foco)
    canOpen(point) {
      return drillableValueOf(index, parts, point, 1) !== null;
    },
  };
}

// Resultado do último clique de cada lista: o chip mostra a contagem sem pedir de novo, avisa quando o número clicado
// não é mais o recalculado e "Voltar ao painel" volta ao endereço exato do painel (aba e filtros). Fica na memória da aba.
/** @type {Map<string, { data: any, origin: string | null, shownValue: number | null }>} */
const rememberedResults = new Map();

/**
 * @param {any} data resposta do POST /rest/dashboards/drilldown @param {string | null} origin endereço do painel
 * @param {number | null} [shownValue] número que o cartão mostrava no clique
 */
export function rememberDrilldownResult(data, origin, shownValue = null) {
  if (!data?.drilldownParam) return;
  rememberedResults.set(data.drilldownParam, { data, origin, shownValue });
  while (rememberedResults.size > 20) rememberedResults.delete(/** @type {string} */ (rememberedResults.keys().next().value));
}

/** @param {string | null | undefined} parameter */
export const rememberedDrilldown = (parameter) => (parameter ? rememberedResults.get(parameter) ?? null : null);

/** Receita que está sendo aberta agora (um segundo clique no mesmo ponto não pede de novo). @type {string | null} */
let pendingRecipe = null;
// pedido lento: depois deste tempo aparece o aviso "Abrindo a lista do painel…" com "Cancelar"
const OPENING_NOTICE_DELAY_MILLISECONDS = 400;

/** Endereço atual (caminho e busca): a lista só abre se a pessoa ainda estiver onde clicou. */
const currentAddress = () => `${window.location.pathname}${window.location.search}`;

/**
 * Pede o ponto ao servidor e abre a lista. Erro vira aviso (ponto sem registros, sem permissão, conjunto grande demais),
 * menos com `silent`: a página /drilldown já mostra o erro no lugar da lista e o aviso repetiria a frase.
 * Se a pessoa saiu da tela (outro item do menu, outro filtro) ou cancelou antes da resposta, a lista não toma a tela.
 * @param {string} recipe
 * @param {{ element?: Element | null, replace?: boolean, origin?: string | null, shownValue?: number | null, silent?: boolean }} [options]
 * @returns {Promise<any | null>}
 */
export async function openDrilldownRecipe(recipe, { element = null, replace = false, origin = null, shownValue = null, silent = false } = {}) {
  if (!recipe || pendingRecipe === recipe) return null;
  pendingRecipe = recipe;
  const startedAt = currentAddress();
  let isCancelled = false;
  /** @type {string | null} */
  let noticeId = null;
  const noticeTimer = setTimeout(() => {
    noticeId = enqueueSnackbar({
      message: t('dashboardDrilldown.opening'), variant: 'info', duration: 60_000, dedupeKey: 'drilldown-opening',
      buttonLabel: t('common.cancel'), onButtonClick: () => { isCancelled = true; },
    });
  }, OPENING_NOTICE_DELAY_MILLISECONDS);
  element?.setAttribute('aria-busy', 'true');
  document.documentElement.dataset.drilldownBusy = 'true';
  try {
    const data = await resolveDrilldown(recipe);
    rememberDrilldownResult(data, origin, shownValue);
    const isStillHere = !isCancelled && currentAddress() === startedAt;
    if (isStillHere && typeof data?.url === 'string' && data.url.startsWith('/')) navigate(data.url, { replace });
    return data;
  } catch (error) {
    if (!silent && !isCancelled && currentAddress() === startedAt) showErrorSnackbar(error);
    throw error;
  } finally {
    clearTimeout(noticeTimer);
    if (noticeId) dismissSnackbar(noticeId);
    pendingRecipe = null;
    element?.removeAttribute('aria-busy');
    delete document.documentElement.dataset.drilldownBusy;
  }
}

/**
 * Abre a lista de um link de ponto na mesma aba.
 * @param {string | null | undefined} href @param {Element | null} [element]
 */
export function openDrilldownHref(href, element = null) {
  const recipe = recipeTextOfHref(href);
  if (!recipe) return;
  openDrilldownRecipe(recipe, { element, origin: currentAddress(), shownValue: shownValueOfHref(href) }).catch(() => {});
}

/** Ctrl/Cmd+Enter no teclado: a mesma lista numa aba nova. @param {string | null | undefined} href */
export function openDrilldownInNewTab(href) {
  if (href) window.open(href, '_blank', 'noopener');
}

/**
 * Clique simples (botão principal, sem modificador): o navegador segue o link sozinho nos outros casos.
 * @param {MouseEvent} event
 */
export const isPlainActivation = (event) => !event.defaultPrevented && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;

/**
 * Clique num link de ponto em HTML (número, item de lista, célula, bloco): abre pela SPA; o resto fica com o navegador.
 * @param {MouseEvent} event @param {string} href
 */
export function handleDrillLinkClick(event, href) {
  if (!isPlainActivation(event)) {
    // Ctrl/Cmd+clique numa linha de tabela que abre o registro: só o link vale
    event.stopPropagation();
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  openDrilldownHref(href, /** @type {Element} */ (event.currentTarget));
}

/**
 * Texto acessível de um ponto ("Visita agendada: 42. Abrir a lista com estes registros").
 * @param {string} label @param {string} valueText
 */
export const pointAriaLabel = (label, valueText) => t('dashboardDrilldown.pointAriaLabel', { label, value: valueText });

/**
 * Link de um ponto em HTML; sem alvo (zero, sem permissão, edição), o mesmo conteúdo sem link.
 * @param {{ point: DrilldownPoint, value?: number | null, label: string, valueText: string, className?: string, as?: string,
 *   stretched?: boolean, hint?: string, title?: string, children?: any, [attribute: string]: any }} props
 *   value: valor de um ponto fora da lista do widget; stretched: o link cobre o item inteiro (o resto do item fica por cima);
 *   title: dica do conteúdo (o valor inteiro de uma moeda compacta), que no link vem antes da dica do clique;
 *   os demais atributos (data-tone, key) vão para o elemento nos dois casos
 */
export function DrillLink({ point, value, label, valueText, className = '', as = 'span', stretched = false, hint, title, children, ...rest }) {
  const drill = useDrilldown();
  const target = drill?.target(point, value);
  if (!target) return html`<${as} class=${className || undefined} title=${title} ...${rest}>${children}<//>`;
  const clickHint = hint ?? t('dashboardDrilldown.tooltipHint');
  return html`<a
    href=${target.href}
    class=${`drill-link ${className}`.trim()}
    data-drillable="true"
    data-stretched=${stretched ? 'true' : undefined}
    aria-label=${pointAriaLabel(label, valueText)}
    title=${title ? `${title} · ${clickHint}` : clickHint}
    onClick=${(/** @type {MouseEvent} */ event) => handleDrillLinkClick(event, target.href)}
    ...${rest}
  >${children}</a>`;
}

/**
 * Teclado, toque e foco dos pontos de um gráfico em SVG: tabindex rotativo (uma parada de Tab), setas pela lógica de
 * chart-keyboard.js, Enter abre e Ctrl/Cmd+Enter abre numa aba nova; no toque o primeiro toque fixa a dica.
 * @param {{ points: ChartPoint[], label: string, orientation?: 'vertical' | 'horizontal',
 *   onFocusPoint?: (point: ChartPoint, element: Element) => void, onBlurPoint?: () => void,
 *   onPin?: (point: ChartPoint, event: MouseEvent) => void, onUnpin?: () => void }} options
 */
export function useChartPointNavigation({ points, label, orientation = 'vertical', onFocusPoint, onBlurPoint, onPin, onUnpin }) {
  const containerRef = useRef(/** @type {Element | null} */ (null));
  const pointerTypeRef = useRef('mouse');
  const [activeId, setActiveId] = useState(/** @type {string | null} */ (null));
  const [pinnedId, setPinnedId] = useState(/** @type {string | null} */ (null));
  const activeIndex = Math.max(0, points.findIndex((point) => point.id === activeId));
  const latest = useRef({ onUnpin });
  latest.current = { onUnpin };

  // tocar fora do gráfico solta a dica fixada
  useEffect(() => {
    if (!pinnedId) return undefined;
    /** @param {PointerEvent} event */
    const handlePointerDown = (event) => {
      const target = /** @type {Element | null} */ (event.target);
      if (target && (containerRef.current?.contains(target) || target.closest?.('[data-chart-tooltip-pinned]'))) return;
      setPinnedId(null);
      latest.current.onUnpin?.();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [pinnedId]);

  /** @param {ChartPoint} point */
  const hrefOf = (point) => point.href ?? point.resolveHref?.() ?? null;

  /** @param {number} index */
  const focusPoint = (index) => {
    const point = points[index];
    if (!point) return;
    setActiveId(point.id);
    const element = containerRef.current?.querySelector(`[data-point-id="${CSS.escape(point.id)}"]`);
    /** @type {any} */ (element)?.focus?.();
  };

  /** @param {KeyboardEvent} event */
  const onKeyDown = (event) => {
    const focused = /** @type {Element | null} */ (event.target);
    const index = points.findIndex((point) => point.id === focused?.getAttribute?.('data-point-id'));
    if (index === -1) return;
    const action = chartKeyboardAction(points, index, event, { orientation });
    if (!action) return;
    event.preventDefault();
    if (action.type === 'move') {
      focusPoint(action.index);
      return;
    }
    const href = hrefOf(points[index]);
    if (action.newTab) openDrilldownInNewTab(href);
    else openDrilldownHref(href, focused);
  };

  return {
    pinnedId,
    clearPin: () => setPinnedId(null),
    /** Propriedades do elemento que agrupa os pontos (o próprio svg). */
    containerProps: {
      ref: containerRef,
      role: 'group',
      'aria-label': points.length > 0 ? `${label}. ${t('dashboardDrilldown.chartKeyboardHint')}` : label,
      onKeyDown: points.length > 0 ? onKeyDown : undefined,
      onPointerDown: (/** @type {PointerEvent} */ event) => {
        pointerTypeRef.current = event.pointerType || 'mouse';
      },
    },
    /**
     * Propriedades do <a> de um ponto.
     * @param {ChartPoint} point @param {{ label: string, valueText: string }} text
     */
    pointProps(point, text) {
      const index = points.indexOf(point);
      return {
        href: point.href ?? undefined,
        class: 'chart-drill-link',
        'data-point-id': point.id,
        'data-drillable': 'true',
        tabindex: index === activeIndex ? '0' : '-1',
        'aria-label': pointAriaLabel(text.label, text.valueText),
        onPointerEnter: point.href ? undefined : (/** @type {PointerEvent} */ event) => {
          const href = hrefOf(point);
          if (href) /** @type {Element} */ (event.currentTarget).setAttribute('href', href);
        },
        onFocus: (/** @type {FocusEvent} */ event) => {
          const element = /** @type {Element} */ (event.currentTarget);
          if (!point.href) {
            const href = hrefOf(point);
            if (href) element.setAttribute('href', href);
          }
          setActiveId(point.id);
          onFocusPoint?.(point, element);
        },
        onBlur: () => onBlurPoint?.(),
        onClick: (/** @type {MouseEvent} */ event) => {
          if (!isPlainActivation(event)) return;
          event.preventDefault();
          event.stopPropagation();
          if (pointerTypeRef.current === 'touch' && pinnedId !== point.id) {
            setPinnedId(point.id);
            onPin?.(point, event);
            return;
          }
          setPinnedId(null);
          openDrilldownHref(hrefOf(point), /** @type {Element} */ (event.currentTarget));
        },
      };
    },
  };
}

/**
 * Faixa da categoria (clique fora das barras): abre a categoria inteira. Não entra na ordem do Tab, que fica com as
 * barras. No toque, a faixa só fixa a dica, como o primeiro toque numa barra.
 * @param {{ href: string, label: string, valueText: string, onTouchPin?: (event: MouseEvent) => void, children?: any }} props
 */
export function ChartBandLink({ href, label, valueText, onTouchPin, children }) {
  const pointerTypeRef = useRef('mouse');
  return html`<a
    href=${href}
    class="chart-drill-link chart-band-link"
    data-drillable="true"
    tabindex="-1"
    aria-label=${pointAriaLabel(label, valueText)}
    onPointerDown=${(/** @type {PointerEvent} */ event) => {
      pointerTypeRef.current = event.pointerType || 'mouse';
    }}
    onClick=${(/** @type {MouseEvent} */ event) => {
      if (!isPlainActivation(event)) return;
      event.preventDefault();
      event.stopPropagation();
      if (pointerTypeRef.current === 'touch' && onTouchPin) {
        onTouchPin(event);
        return;
      }
      openDrilldownHref(href, /** @type {Element} */ (event.currentTarget));
    }}
  >${children}</a>`;
}
